import React, { useState, useEffect, useMemo } from "react";
import apiAdmin from '../../services/api-admin';
import {
    List, ListItemButton, ListItemIcon, ListItemText, Checkbox, Button, Autocomplete, TextField, createFilterOptions,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import SpinnerLoader from '../layout/spinner-loader';

const OutstandingChecksTeamMembers = (props) => {
    const {watch, handleSubmit, setValue, reset, formState: { errors }, control } = useForm({ defaultValues: { account: null, teamMembers: [] } });
    const [accounts, setAccounts] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const filter = createFilterOptions();

    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [checked, setChecked] = useState([]);
    const [leftSearch, setLeftSearch] = useState("");
    const [rightSearch, setRightSearch] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingAccount, setPendingAccount] = useState(null);
    const [originalSelectedBankTeamMembers, setOriginalSelectedBankTeamMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const selectedAccount = watch("account");

    // Helper arrays for calculating transfer operations
    const leftChecked = useMemo(() => left.filter((item) => checked.includes(item.id)), [left, checked]);
    const rightChecked = useMemo(() => right.filter((item) => checked.includes(item.id)), [right, checked]);

    // Filtered lists shown to user based on autocomplete inputs
    const filteredLeft = useMemo(() =>
        left.filter((item) => item.label.toLowerCase().includes(leftSearch.toLowerCase())),
        [left, leftSearch]
    );

    const filteredRight = useMemo(() =>
        right.filter((item) => item.label.toLowerCase().includes(rightSearch.toLowerCase())),
        [right, rightSearch]
    );

    const hasTeamMemberChanges = useMemo(() => {
        const current = [...right.map(x => x.id)].sort();
        const original = [...originalSelectedBankTeamMembers].sort();
        return JSON.stringify(current) !== JSON.stringify(original);
    }, [right, originalSelectedBankTeamMembers]);

    useEffect(() => {
        setLoading(true);
        var promises = [];
        const getBankAccounts = apiAdmin.get(`/bank-accounts`);
        promises.push(getBankAccounts);

        const getTeamMembers = apiAdmin.get(`/team-members`);
        promises.push(getTeamMembers);

        Promise.all(promises)
            .then((responses) => {
                setAccounts(responses[0].data.sort((a, b) => a.name.localeCompare(b.name)));
                setTeamMembers(responses[1].data.map((a) => ({
                    id: a.teamMemberId, label: `${a.firstName} ${a.lastName}`
                })));
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!selectedAccount || !teamMembers.length) return;

        const getAssignedTeamMembers = async (selectedAccount) => {
            apiAdmin.get(`/bank-account-team-members/${selectedAccount.bankAccountId}`)
                .then((response) => {
                    var selected = response.data.map(t => ({ id: t.teamMemberId, label: `${t.teamMember.firstName} ${t.teamMember.lastName}` }));
                    setRight(selected);
                    setLeft(teamMembers.filter((item) => !(selected.map(t => (t.id))).includes(item.id)));
                    setValue("teamMembers", selected.map(t => t.id), { shouldDirty: false });
                    setOriginalSelectedBankTeamMembers(selected.map(t => t.id));
                });
        };
        getAssignedTeamMembers(selectedAccount);

    }, [selectedAccount, teamMembers, setValue])

    useEffect(() => {
        setValue(
            "teamMembers",
            right.map(x => x.id),
            {
                shouldDirty: true,
                shouldValidate: true
            }
        );
    }, [right, setValue]);

    const handleToggle = (id) => () => {
        const currentIndex = checked.indexOf(id);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(id);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    // Move checked items from Left list to Right list
    const handleMoveToRight = () => {
        setRight(right.concat(leftChecked).sort((a, b) => a.label.localeCompare(b.label)));
        setLeft(left.filter((item) => !checked.includes(item.id)));
        setChecked(checked.filter((id) => !left.some((item) => item.id === id)));
    };

    // Move checked items from Right list to Left list
    const handleMoveToLeft = () => {
        setLeft((left.concat(rightChecked)).sort((a, b) => a.label.localeCompare(b.label)));
        setRight(right.filter((item) => !checked.includes(item.id)));
        setChecked(checked.filter((id) => !right.some((item) => item.id === id)));
    };


    const onSubmit = data => {
        const saveAccountTeamMembers = async () => {
            await apiAdmin.post(`/bank-account-team-members/${selectedAccount.bankAccountId}`, data.teamMembers)
                .then((response) => {
                    if (response.statusText === "OK") {
                        setSubmitted(true);
                        setLoading(false);
                        reset();
                    }
                });
        }
        setLoading(true);
        saveAccountTeamMembers();       
    }


    const customList = (title, items, searchVal, setSearchVal) => (
        <div className="d-flex flex-column">         
            <h4>{title} ({items.length})</h4>

            {/* Autocomplete Input acting as a Filter */}
            <Autocomplete
                freeSolo
                disableClearable
                options={items.map((option) => option.label)}
                inputValue={searchVal}
                onInputChange={(_, newInputValue) => setSearchVal(newInputValue)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Search items..."
                        variant="outlined"
                        size="small"
                        InputProps={{ ...params.InputProps, type: 'search' }}
                    />
                )}
                sx={{ mb: 2 }}
            />

            {/* Item Checkbox List Wrapper */}
            <div className="border border-black-40 rounded-1" style={{ maxHeight: 400, overflowY: "auto" }} >
                <List dense component="div" role="list">
                    {items.map((item) => {
                        const labelId = `transfer-list-item-${item.id}-label`;
                        return (
                            <ListItemButton key={item.id} role="listitem" onClick={handleToggle(item.id)} sx={{ py: .25, minHeight: 28 }} >
                                <ListItemIcon>
                                    <Checkbox
                                        size="small"
                                        checked={checked.includes(item.id)}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{ "aria-labelledby": labelId }}
                                        sx={{ p: .25 }}
                                    />
                                </ListItemIcon>
                                <ListItemText id={labelId} primary={item.label} />
                            </ListItemButton>
                        );
                    })}
                </List>
            </div>
        </div>
    );

    return (
        <div className="vh-90">
            <title>Outstanding Checks Account Team Members</title>
            <h1>Outstanding Checks Account Team Members</h1>

            <form onSubmit={handleSubmit(onSubmit)}>
                {submitted && <><div className="alert alert-success alert-dismissible" role="alert">Bank account team members have been saved successfully!</div></>}
                {loading && <SpinnerLoader />}

                {!loading && <>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-12 col-lg-8">
                                <Controller
                                    name="account"
                                    control={control}
                                    rules={{ required: true }}
                                    defaultValue={null}
                                    render={({ field }) => (

                                        <Autocomplete
                                            value={field.value}
                                            isOptionEqualToValue={(option, value) => { return option.bankAccountId === value.bankAccountId }}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string' ? option : `${option.name} - ${option.accountNumberRedacted}`

                                            }
                                            options={accounts}
                                            autoComplete
                                            includeInputInList
                                            filterOptions={(o, p) => {
                                                const filtered = filter(o, p);
                                                return filtered;
                                            }}
                                            noOptionsText="No accounts found."
                                            onChange={(_, newAccount) => {
                                                setSubmitted(false);
                                                if (hasTeamMemberChanges) {
                                                    setPendingAccount(newAccount);
                                                    setConfirmOpen(true);
                                                    return;
                                                }
                                                field.onChange(newAccount);
                                                reset({
                                                    account: newAccount,
                                                    teamMembers: []
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Search accounts" fullWidth />
                                            )}
                                            renderOption={(props, option) => {
                                                const { key, ...optionProps } = props;
                                                return (
                                                    <li key={option.bankAccountId} {...optionProps}>
                                                        {option.name} - {option.accountNumberRedacted}
                                                    </li>
                                                );
                                            }}
                                        />

                                    )} />
                                {errors?.account?.type === "required" && <div className="small text-danger">Please make a selection.</div>}

                            </div>
                        </div>
                    </div>
        

                    {selectedAccount && <div className="col-12 col-lg-8">
                    
                        <div className="row">
                            <div className="col-5">
                                {customList("Available", filteredLeft, leftSearch, setLeftSearch)}
                            </div>
                            <div className="col-2 text-center">
                                <div className="flex flex-column mt-5 pt-5">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleMoveToRight}
                                    disabled={leftChecked.length === 0}
                                    aria-label="move selected right"
                                    sx={{ my: 0.5 }}
                                >
                                    &gt;
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleMoveToLeft}
                                    disabled={rightChecked.length === 0}
                                    aria-label="move selected left"
                                    sx={{ my: 0.5 }}
                                >
                                    &lt;
                                    </Button>
                                </div>
                            </div>
                            <div className="col-5">
                                {customList("Selected", filteredRight, rightSearch, setRightSearch)}
                                <div className="text-center pt-3">
                                    <button type="submit" className="btn btn-primary me-4" disabled={!selectedAccount}>Save</button>
                                </div>
                            </div>
                        </div>
                    </div>}

                    <Dialog
                        open={confirmOpen}
                        onClose={() => setConfirmOpen(false)}
                    >
                        <DialogTitle>Unsaved Changes</DialogTitle>

                        <DialogContent>
                            <DialogContentText>
                                You have unsaved team member changes. If you switch bank
                                accounts now, your changes will be lost.
                            </DialogContentText>
                        </DialogContent>

                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setPendingAccount(null);
                                    setConfirmOpen(false);
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                color="error"
                                variant="contained"
                                onClick={() => {
                                    setValue("account", pendingAccount, {
                                        shouldDirty: false
                                    });

                                    setPendingAccount(null);
                                    setConfirmOpen(false);
                                }}
                            >
                                Discard Changes
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>}
            </form>
        </div>
    );
};

export default OutstandingChecksTeamMembers;
